import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import languagesMap from '@chancenwerk/assets/languages.json';
import '@chancenwerk/components/chancen-component-address-picker.js';
import { ContactMethod } from '@chancenwerk/db/enums/enums.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import ImageStudent from './../assets/Ausweisdokument-small-min.png';
import ImageFamily from './../assets/Einfuhrung_rund_um_die_Lernkaskade-small-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

const languages = [
  ...new Set(
    Object.values(languagesMap)
      .filter(lang => lang['639-1'])
      .map(lang => lang.de[0]),
  ),
];

/**
 * @customElement anmeldung-component-formular-personal-info
 */
@customElement('anmeldung-component-formular-personal-info')
export class AnmeldungPageRegistrationPersonalInfo extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) legalGuardian1AddressDifferent!: RXController<boolean>;
  @property({ attribute: false }) legalGuardian2AddressDifferent!: RXController<boolean>;

  @property({ attribute: false }) hide = new RXController<
    ('CONTACT_PREFERENCE' | 'LEGAL_GUARDIAN' | 'NATIONALITY' | 'BIRTHDAY')[]
  >(this, []);

  override render() {
    return html`
      <cr-flex space="xl">
        <cr-flex space="xxl">${[this._getStudentSection(), this._getParentSection()]} </cr-flex>

        <cr-flex horizontal end crossaxis-center space="sm">
          <cr-button
            @click=${async () => {
              this.form.validate().catch(console.error);
            }}
          >
            ${this.translation.translate('next')}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getStudentSection() {
    return html`
      <cr-flex class="section-container" space="lg">
        <cr-image disabled .value=${ImageStudent} mode="contain"> </cr-image>

        <cr-flex space="lg" w100>
          ${this.schoolLevel === 'us'
            ? html` <cr-text>
                Um ihr Kind für die Lernkaskade von Chancenwerk e.V. anzumelden, benötigen wir folgende persönlichen
                Informationen von Ihrem Kind und Ihnen. Bitte füllen Sie dazu die nachstehenden Felder aus:
              </cr-text>`
            : nothing}
          <cr-flex space="sm">
            <cr-h4 align="center" ${css({ padding: `0 ${this.spacing.sm}`, marginTop: this.spacing.xs })}>
              ${this.translation.translate('step2', this.schoolLevel, 'sectionHeaderChild')}
            </cr-h4>
          </cr-flex>

          <!--<cr-text ${css({ fontSize: '13px' })}>mit * gekennzeichnete Felder sind Pflichtfelder.</cr-text>-->

          <cr-flex class="sc-content">
            <cr-flex space="lg">
              <cr-flex space="md">
                <cr-input
                  ${this.form.field('vorname')}
                  placeholder="${this.translation.translate('firstName')} *"
                ></cr-input>
                <cr-input
                  ${this.form.field('nachname')}
                  placeholder="${this.translation.translate('lastName')} *"
                ></cr-input>
                <cr-flex horizontal crossaxis-center crossaxis-end space="md" class="mobile--flex">
                  ${!this.hide.value.includes('BIRTHDAY')
                    ? html` <cr-flex ${css({ width: '50%' })} class="mobile--fullwidth">
                        <cr-date-picker-dropdown
                          .end-year=${DateTime.now().plus({ years: 10 }).year}
                          ${this.form.field('geburtstag')}
                          placeholder=${this.translation.translate('birthday') + ' *'}
                          click-to-save
                          clearable
                        ></cr-date-picker-dropdown>
                      </cr-flex>`
                    : nothing}
                  <cr-flex
                    ${css(!this.hide.value.includes('BIRTHDAY') ? { width: '50%' } : { width: '100%' })}
                    class="mobile--fullwidth"
                  >
                    <cr-dropdown-selection
                      ${this.form.field('geschlecht')}
                      placeholder=${this.translation.translate('gender')}
                      .selectables=${[
                        this.translation.translate('genderMale'),
                        this.translation.translate('genderFemale'),
                        this.translation.translate('genderDiverse'),
                      ]}
                    ></cr-dropdown-selection
                  ></cr-flex>
                </cr-flex>
                ${!this.hide.value.includes('NATIONALITY')
                  ? html`<cr-dropdown-search-selection
                      clearable
                      .search=${(str: Maybe<string>) => {
                        if (!str || str.length === 0) {
                          return Object.values(languages);
                        }
                        return Object.values(languages).filter(lang => lang.toLowerCase().includes(str.toLowerCase()));
                      }}
                      placeholder=${'gesprochene Sprache zu Hause'}
                      ${this.form.field('spokenLanguage')}
                    ></cr-dropdown-search-selection>`
                  : nothing}
                ${this.schoolLevel === 'os'
                  ? html`
                      <cr-flex space="md" class="mobile--flex">
                        <cr-input ${this.form.field('email')} placeholder="E-Mail *" type="email"></cr-input>

                        <cr-input
                          ${this.form.field('handynummer')}
                          placeholder=${this.translation.translate('mobile') + ' *'}
                          type="tel"
                          pattern="[0-9]*"
                        ></cr-input>
                      </cr-flex>
                    `
                  : nothing}
              </cr-flex>

              <chancen-component-address-picker
                required
                hide-all-countries
                ${this.form.field('adresse')}
                @province-picked=${e => {
                  if (!this.form.document) return;
                  this.form.document.bundesland = e.detail.value;
                }}
              ></chancen-component-address-picker>
              ${this.schoolLevel === 'os' ? this._getPreferedContactWay() : nothing}
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  // eslint-disable-next-line complexity
  private _getSingleParentCol(legalGuardianKey: 'legalGuardian1' | 'legalGuardian2') {
    return html`
      <cr-flex space="md">
        <cr-flex space="sm">
          <cr-h4 ${css({ padding: `0 ${this.spacing.sm}`, marginTop: this.spacing.xs })}>
            Erziehungsberechtigte Person ${legalGuardianKey === 'legalGuardian1' ? 1 : 2}
          </cr-h4>
        </cr-flex>
        <cr-input
          ${this.form.field(legalGuardianKey, 'vorname')}
          placeholder="${this.translation.translate('firstName')} ${legalGuardianKey === 'legalGuardian1' ? '*' : ''}"
        ></cr-input>
        <cr-input
          ${this.form.field(legalGuardianKey, 'nachname')}
          placeholder="${this.translation.translate('lastName')} ${legalGuardianKey === 'legalGuardian1' ? '*' : ''}"
        ></cr-input>
        <cr-dropdown-search-selection
          clearable
          .search=${(str: Maybe<string>) => {
            if (!str || str.length === 0) {
              return Object.values(languages);
            }
            return Object.values(languages).filter(lang => lang.toLowerCase().includes(str.toLowerCase()));
          }}
          placeholder=${'gesprochene Sprache zu Hause'}
          ${this.form.field(legalGuardianKey, 'spokenLanguage')}
        ></cr-dropdown-search-selection>
        <cr-input
          ${this.form.field(legalGuardianKey, 'beruf')}
          placeholder="${this.translation.translate('job')}"
        ></cr-input>
        <cr-flex
          crossaxis-center
          space="sm"
          ${css({ padding: this.spacing.sm, border: '5px solid #99c1bd', borderRadius: '10px' })}
        >
          <cr-input
            ${css({ width: '100%' })}
            ${this.form.field(legalGuardianKey, 'handynummer')}
            placeholder="${this.translation.translate('mobile')} ${legalGuardianKey === 'legalGuardian1' ? '*' : ''}"
            type="tel"
            pattern="[0-9]*"
          ></cr-input>
          <cr-text>oder</cr-text>
          <cr-input
            ${css({ width: '100%' })}
            ${this.form.field(legalGuardianKey, 'festnetznummer')}
            placeholder="${this.translation.translate('phone')} ${legalGuardianKey === 'legalGuardian1' ? '*' : ''}"
            type="tel"
            pattern="[0-9]*"
          ></cr-input>
        </cr-flex>
        ${this.schoolLevel === 'us'
          ? html`
              <cr-input
                ${this.form.field(legalGuardianKey, 'email')}
                placeholder="E-Mail ${legalGuardianKey === 'legalGuardian1' ? '*' : ''}"
                type="email"
              ></cr-input>
            `
          : nothing}

        <cr-dropdown-selection
          ${this.form.field(legalGuardianKey, 'geschlecht')}
          placeholder=${this.translation.translate('gender')}
          .selectables=${[
            this.translation.translate('genderMale'),
            this.translation.translate('genderFemale'),
            this.translation.translate('genderDiverse'),
          ]}
        ></cr-dropdown-selection>
        <cr-flex padding="sm" w100>
          <cr-toggle-button
            .value=${legalGuardianKey === 'legalGuardian1'
              ? this.legalGuardian1AddressDifferent.value
              : this.legalGuardian2AddressDifferent.value}
            @value-picked=${(e: ValueEvent<boolean>) => {
              (legalGuardianKey === 'legalGuardian1'
                ? this.legalGuardian1AddressDifferent
                : this.legalGuardian2AddressDifferent
              ).next(e.detail.value);
              this.requestUpdate();
            }}
            .label=${this.translation.translate('adress', 'different')}
          ></cr-toggle-button>
        </cr-flex>
        ${(
          legalGuardianKey === 'legalGuardian1'
            ? this.legalGuardian1AddressDifferent.value
            : this.legalGuardian2AddressDifferent.value
        )
          ? this._getAdressSection(legalGuardianKey, this.translation.translate('step2', 'parentAddress'))
          : nothing}
      </cr-flex>
    `;
  }

  private _getParentSection() {
    if (this.hide.value.includes('LEGAL_GUARDIAN')) return nothing;

    return html`
      <cr-flex class="section-container" space="lg">
        <cr-image disabled .value=${ImageFamily} mode="contain"> </cr-image>

        <cr-flex space="lg" w100>
          <cr-h4 align="center" ${css({ padding: `0 ${this.spacing.sm} ${this.spacing.sm} ${this.spacing.sm}` })}>
            ${this.translation.translate('step2', 'legalGuardianInformation')}
          </cr-h4>
          ${this.schoolLevel == 'os'
            ? html`<cr-text ${css({ textAlign: 'center' })}>
                Es reicht aus, wenn du nur eine erziehungsberechtigte Person angibst.
              </cr-text>`
            : nothing}
          <cr-flex class="sc-content" ${css({ padding: this.spacing.md })}>
            <cr-grid .columns=${this._size.mobile ? '1' : '2'} space="md" ${css({ backgroundColor: 'white' })}>
              ${[this._getSingleParentCol('legalGuardian1'), this._getSingleParentCol('legalGuardian2')]}
            </cr-grid>

            <cr-flex space="md">
              ${this.schoolLevel === 'us'
                ? html` <cr-text>
                    Damit wir Sie zuverlässig auf dem Postweg erreichen können, möchten wir Sie bitten, dass Sie hier
                    den Namen eintragen, der auf Ihrem Briefkasten steht, sofern dieser von den eingetragenen Nachnamen
                    von Ihnen oder Ihrem Kind abweicht.
                  </cr-text>`
                : nothing}

              <cr-input
                placeholder=${this.schoolLevel === 'us'
                  ? 'Abweichender Name auf Ihrem Briefkasten?'
                  : 'Abweichender Name auf dem Briefkasten?'}
                ${this.form.field('zustellbesonderheiten')}
                ${css({ width: '100%' })}
              ></cr-input>
              ${this.schoolLevel === 'us' ? this._getPreferedContactWay() : nothing}
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getPreferedContactWay() {
    if (this.hide.value.includes('CONTACT_PREFERENCE')) return nothing;

    if (this.schoolLevel === 'os') {
      return html`
        <cr-flex space="md" crossaxis-center>
          <cr-flex horizontal crossaxis-center space="sm">
            <cr-text> Wir kontaktieren dich über Handy und E-Mail. </cr-text>
          </cr-flex>
        </cr-flex>
      `;
    }

    const values = Object.values(ContactMethod).filter(
      v =>
        (v !== ContactMethod.LANDLINE || this.schoolLevel !== 'os') &&
        v !== ContactMethod.MESSENGER &&
        v !== ContactMethod.LETTER,
    );
    return html`
      <cr-flex space="md" crossaxis-center>
        <cr-flex horizontal crossaxis-center space="sm">
          <cr-text> Kontaktiert werde ich am liebsten über </cr-text>
          <cr-icon
            ${css({
              padding: this.spacing.xs,

              color: this.colors.accent.shade(500),
              cursor: 'pointer',
            })}
          >
            info
            <cr-tooltip clickable>
              <cr-text ${css({ maxWidth: '400px' })}>
                Teilen Sie uns hier gerne mit, auf welchen Wegen wir Sie zukünftig am besten erreichen können.
                Sie können dabei auch mehrere Wege auswählen. Unabhängig davon erhalten Sie nach der Anmeldung
                eine Bestätigung per Mail.
              </cr-text>
            </x-tooltip>
          </cr-icon>
        </cr-flex>
        <cr-flex horizontal space="md" padding="sm" center crossaxis-center wrap>
          ${repeat(
            values,
            c =>
              html`
                <cr-checkbox
                  .value=${this.student.value.legalGuardianContactMethods?.includes(c)}
                  @value-picked=${e => {
                    const index = this.student.value.legalGuardianContactMethods?.findIndex(contact => contact === c);
                    if (index === undefined) return;

                    if (index === -1 && e.detail.value) this.student.value.legalGuardianContactMethods?.push(c);
                    if (index !== -1 && !e.detail.value)
                      this.student.value.legalGuardianContactMethods?.splice(index, 1);
                    this.requestUpdate();
                  }}
                  label=${c}
                ></cr-checkbox>
              `,
          )}
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getAdressSection(relativeAddressFormKey: 'legalGuardian1' | 'legalGuardian2', headerText: Renderable) {
    return html`
      <cr-flex space="md">
        <cr-h4> ${headerText} </cr-h4>
        <chancen-component-address-picker
          hide-all-countries
          ${this.form.field(relativeAddressFormKey, 'adresse')}
        ></chancen-component-address-picker>
      </cr-flex>
    `;
  }
}
